'use client'

import { useState, useRef, useEffect } from 'react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { Progress } from '../ui/progress'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Textarea } from '../ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { sendSmsSchema, type SendSmsFormData } from '../../../lib/schemas'
import {
  MessageSquare, Plus, X, Bold, Italic, Strikethrough,
  Paperclip, CheckCircle2, XCircle, Loader2,
} from 'lucide-react'
import { useMutation, useQuery } from '@tanstack/react-query'
import api from '../../lib/api'
import { devicesQueryOptions, whatsappGroupsQueryOptions } from '@/lib/queries'
import DevicePicker from './DevicePicker'
import { Label } from '../ui/label'

type SendState = 'idle' | 'sending' | 'success' | 'error'

interface Attachment {
  base64: string
  type: 'image' | 'document'
  filename: string
  mimeType: string
}

export default function SendSms() {
  const [recipients, setRecipients] = useState<string[]>([''])
  const [recipientMode, setRecipientMode] = useState<'phone' | 'group'>('phone')
  const [selectedGroupId, setSelectedGroupId] = useState('')
  const [attachment, setAttachment] = useState<Attachment | null>(null)
  const [sendState, setSendState] = useState<SendState>('idle')
  const [sendError, setSendError] = useState<string>('')
  const [progress, setProgress] = useState(0)
  const progressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  const { data: devices } = useQuery({
    ...devicesQueryOptions(),
    select: (data) => (Array.isArray(data) ? data : []),
  })

  const { mutate: sendSms } = useMutation({
    mutationKey: ['send-sms'],
    mutationFn: (data: SendSmsFormData) =>
      api.post(`/gateway/devices/${data.deviceId}/send-sms`, {
        deviceId: data.deviceId,
        recipient: data.recipients[0],
        message: data.message,
      }),
  })

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
    getValues,
  } = useForm<SendSmsFormData>({
    resolver: zodResolver(sendSmsSchema),
    defaultValues: {
      deviceId: Array.isArray(devices) && devices.length === 1 ? devices[0]?.id : undefined,
      recipients: [''],
      message: '',
    },
  })

  const selectedDeviceId = watch('deviceId')
  const selectedDevice = Array.isArray(devices)
    ? (devices as any[]).find((d: any) => d.id === selectedDeviceId)
    : null
  const isWhatsApp = selectedDevice?.gatewayType === 'whatsapp'

  const { data: groups, isLoading: groupsLoading } = useQuery({
    ...whatsappGroupsQueryOptions(isWhatsApp && recipientMode === 'group' ? selectedDeviceId : undefined),
    select: (data) => (Array.isArray(data) ? data : []),
  })

  // Group sending only applies to WhatsApp — fall back to phone mode otherwise
  useEffect(() => {
    if (!isWhatsApp && recipientMode === 'group') {
      setRecipientMode('phone')
      setSelectedGroupId('')
    }
  }, [isWhatsApp, recipientMode])

  // Animate progress bar while sending
  useEffect(() => {
    if (sendState === 'sending') {
      setProgress(0)
      let current = 0
      progressTimerRef.current = setInterval(() => {
        // Ease toward 85% — never reaches 100 until we get a response
        current += (85 - current) * 0.06
        setProgress(Math.min(current, 85))
      }, 300)
    } else {
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current)
        progressTimerRef.current = null
      }
      if (sendState === 'success') setProgress(100)
    }
    return () => {
      if (progressTimerRef.current) clearInterval(progressTimerRef.current)
    }
  }, [sendState])

  const startSend = () => {
    setSendError('')
    setSendState('sending')
  }

  const finishSend = (ok: boolean, errorMsg?: string) => {
    if (ok) {
      setSendState('success')
      setTimeout(() => {
        const deviceId = getValues('deviceId')
        setSendState('idle')
        setRecipients([''])
        setSelectedGroupId('')
        setAttachment(null)
        reset({ deviceId, recipients: [''], message: '' })
      }, 2000)
    } else {
      setSendError(errorMsg || 'Something went wrong')
      setSendState('error')
    }
  }

  const applyFormat = (marker: string) => {
    const textarea = textareaRef.current
    if (!textarea) return
    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const value = textarea.value
    const newValue = value.slice(0, start) + marker + value.slice(start, end) + marker + value.slice(end)
    setValue('message', newValue)
    setTimeout(() => {
      textarea.focus()
      textarea.setSelectionRange(start + marker.length, end + marker.length)
    }, 0)
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      const base64 = (reader.result as string).split(',')[1]
      setAttachment({
        base64,
        type: file.type.startsWith('image/') ? 'image' : 'document',
        filename: file.name,
        mimeType: file.type,
      })
    }
    reader.readAsDataURL(file)
    e.target.value = ''
  }

  const addRecipient = () => {
    const next = [...recipients, '']
    setRecipients(next)
    setValue('recipients', next)
  }

  const removeRecipient = (index: number) => {
    if (recipients.length > 1) {
      const next = recipients.filter((_: string, i: number) => i !== index)
      setRecipients(next)
      setValue('recipients', next)
    }
  }

  const updateRecipient = (index: number, value: string) => {
    const next = [...recipients]
    next[index] = value
    setRecipients(next)
    setValue('recipients', next)
  }

  // Group sends bypass the phone-number-shaped `recipients` field, so they're
  // validated and submitted separately rather than through RHF's zod schema.
  const submitGroup = () => {
    const deviceId = getValues('deviceId')
    const message = getValues('message')
    if (!deviceId) return finishSendError('Choose a sender first')
    if (!selectedGroupId) return finishSendError('Please select a group')
    if (!attachment && !message) return finishSendError('Message is required')

    startSend()
    const to = selectedGroupId
    if (attachment) {
      api.post(`/whatsapp/devices/${deviceId}/send-media`, {
        to,
        type: attachment.type,
        data: attachment.base64,
        caption: message || undefined,
        filename: attachment.filename,
        mimeType: attachment.mimeType,
      })
        .then(() => finishSend(true))
        .catch((err: any) => finishSend(false, err.response?.data?.message || err.message))
    } else {
      api.post(`/whatsapp/devices/${deviceId}/send-text`, { to, message })
        .then(() => finishSend(true))
        .catch((err: any) => finishSend(false, err.response?.data?.message || err.message))
    }
  }

  const finishSendError = (msg: string) => {
    setSendError(msg)
    setSendState('error')
  }

  const onSubmit = (data: SendSmsFormData) => {
    startSend()
    if (isWhatsApp) {
      const to = data.recipients[0]
      if (attachment) {
        api.post(`/whatsapp/devices/${data.deviceId}/send-media`, {
          to,
          type: attachment.type,
          data: attachment.base64,
          caption: data.message || undefined,
          filename: attachment.filename,
          mimeType: attachment.mimeType,
        })
          .then(() => finishSend(true))
          .catch((err: any) => finishSend(false, err.response?.data?.message || err.message))
      } else {
        api.post(`/whatsapp/devices/${data.deviceId}/send-text`, {
          to,
          message: data.message,
        })
          .then(() => finishSend(true))
          .catch((err: any) => finishSend(false, err.response?.data?.message || err.message))
      }
    } else {
      sendSms(data, {
        onSuccess: () => finishSend(true),
        onError: (err: any) => finishSend(false, err.response?.data?.message || err.message),
      })
    }
  }

  const { ref: registerRef, ...registerRest } = register('message')

  const dialogOpen = sendState !== 'idle'

  return (
    <div>
      {/* Send progress dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open && sendState !== 'sending') setSendState('idle') }}>
        <DialogContent className='sm:max-w-sm' onPointerDownOutside={(e) => { if (sendState === 'sending') e.preventDefault() }}>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2'>
              {sendState === 'sending' && <Loader2 className='h-5 w-5 animate-spin text-blue-500' />}
              {sendState === 'success' && <CheckCircle2 className='h-5 w-5 text-green-500' />}
              {sendState === 'error' && <XCircle className='h-5 w-5 text-red-500' />}
              {sendState === 'sending' && 'Sending message…'}
              {sendState === 'success' && 'Message sent!'}
              {sendState === 'error' && 'Failed to send'}
            </DialogTitle>
          </DialogHeader>

          <div className='space-y-4 py-2'>
            <Progress value={progress} className='h-2 transition-all duration-300' />

            {sendState === 'sending' && (
              <p className='text-sm text-muted-foreground text-center'>
                {isWhatsApp ? 'Sending via WhatsApp…' : 'Sending SMS…'} please wait.
              </p>
            )}

            {sendState === 'success' && (
              <p className='text-sm text-green-600 text-center'>
                {isWhatsApp ? 'WhatsApp message delivered.' : 'SMS sent successfully.'}
              </p>
            )}

            {sendState === 'error' && (
              <>
                <p className='text-sm text-red-600 text-center break-words'>{sendError}</p>
                <Button className='w-full' onClick={() => setSendState('idle')}>
                  Dismiss
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader>
          <div className='flex items-center gap-2'>
            <MessageSquare className='h-5 w-5' />
            <CardTitle>Send Message</CardTitle>
          </div>
          <CardDescription>Send a message to any recipient(s)</CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={
              isWhatsApp && recipientMode === 'group'
                ? (e) => { e.preventDefault(); submitGroup() }
                : handleSubmit(onSubmit)
            }
            className='space-y-4'
          >
            <div className='space-y-4'>
              <div>
                <Label htmlFor='send-device' className='mb-1.5 block'>Send from</Label>
                <Controller
                  name='deviceId'
                  control={control}
                  render={({ field }) => (
                    <DevicePicker
                      id='send-device'
                      devices={devices as any[]}
                      value={field.value ?? ''}
                      onChange={field.onChange}
                    />
                  )}
                />
                {errors.deviceId && (
                  <p className='text-sm text-destructive mt-1'>{errors.deviceId.message}</p>
                )}
              </div>

              <div className='space-y-2'>
                {isWhatsApp && (
                  <div className='flex items-center gap-4 text-sm'>
                    <label className='flex items-center gap-1.5 cursor-pointer'>
                      <input
                        type='radio'
                        name='recipientMode'
                        checked={recipientMode === 'phone'}
                        onChange={() => setRecipientMode('phone')}
                      />
                      Phone number
                    </label>
                    <label className='flex items-center gap-1.5 cursor-pointer'>
                      <input
                        type='radio'
                        name='recipientMode'
                        checked={recipientMode === 'group'}
                        onChange={() => setRecipientMode('group')}
                      />
                      WhatsApp group
                    </label>
                  </div>
                )}

                {isWhatsApp && recipientMode === 'group' ? (
                  <>
                    <Select value={selectedGroupId} onValueChange={setSelectedGroupId}>
                      <SelectTrigger aria-label='WhatsApp group'>
                        <SelectValue placeholder={groupsLoading ? 'Loading groups…' : 'Select a group'} />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.isArray(groups) && groups.length > 0 ? (
                          groups.map((group: any) => (
                            <SelectItem key={group.id} value={group.id}>
                              {group.name}{typeof group.participantsCount === 'number' ? ` (${group.participantsCount})` : ''}
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value='no-groups' disabled>
                            {groupsLoading ? 'Loading groups…' : 'No groups found'}
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </>
                ) : (
                  <>
                    {recipients.map((recipient: string, index: number) => (
                      <div key={index}>
                        <div className='flex gap-2'>
                          <Input
                            type='tel'
                            placeholder='Phone Number'
                            value={recipient}
                            onChange={(e) => updateRecipient(index, e.target.value)}
                          />
                          <Button
                            type='button'
                            variant='ghost'
                            size='icon'
                            aria-label='Remove recipient'
                            onClick={() => removeRecipient(index)}
                            disabled={index === 0 && recipients.length === 1}
                          >
                            <X className='h-4 w-4' />
                          </Button>
                        </div>
                        {errors.recipients?.[index] && (
                          <p className='text-sm text-destructive'>{errors.recipients[index].message}</p>
                        )}
                      </div>
                    ))}
                    <Button type='button' variant='outline' size='sm' onClick={addRecipient} className='w-full'>
                      <Plus className='h-4 w-4 mr-2' />
                      Add Recipient
                    </Button>
                    {errors.recipients && (
                      <p className='text-sm text-destructive'>{errors.recipients.message}</p>
                    )}
                  </>
                )}
              </div>

              <div>
                {isWhatsApp && (
                  <div className='flex items-center gap-1 mb-1 border rounded-t-md px-2 py-1 bg-muted/40'>
                    <Button type='button' variant='ghost' size='sm' className='h-7 w-7 p-0' title='Bold (*text*)' onClick={() => applyFormat('*')}>
                      <Bold className='h-3.5 w-3.5' />
                    </Button>
                    <Button type='button' variant='ghost' size='sm' className='h-7 w-7 p-0' title='Italic (_text_)' onClick={() => applyFormat('_')}>
                      <Italic className='h-3.5 w-3.5' />
                    </Button>
                    <Button type='button' variant='ghost' size='sm' className='h-7 w-7 p-0' title='Strikethrough (~text~)' onClick={() => applyFormat('~')}>
                      <Strikethrough className='h-3.5 w-3.5' />
                    </Button>
                  </div>
                )}
                <Textarea
                  placeholder={isWhatsApp ? 'WhatsApp message (optional if attaching a file)' : 'Message'}
                  {...registerRest}
                  ref={(el) => { registerRef(el); textareaRef.current = el }}
                  rows={4}
                  className={isWhatsApp ? 'rounded-t-none' : ''}
                />
                {errors.message && (
                  <p className='text-sm text-destructive mt-1'>{errors.message.message}</p>
                )}
              </div>

              {isWhatsApp && (
                <div>
                  <label className='flex items-center gap-2 cursor-pointer text-sm text-muted-foreground hover:text-foreground transition-colors'>
                    <div className='flex items-center gap-2 px-3 py-2 border rounded-md hover:bg-muted/50 transition-colors w-full'>
                      <Paperclip className='h-4 w-4 flex-shrink-0' />
                      {attachment ? (
                        <span className='truncate flex-1'>{attachment.filename}</span>
                      ) : (
                        <span className='flex-1'>Attach image (JPG, PNG, GIF, WebP) or PDF</span>
                      )}
                      {attachment && (
                        <button
                          type='button'
                          onClick={(e) => { e.preventDefault(); setAttachment(null) }}
                          className='ml-auto text-muted-foreground hover:text-destructive flex-shrink-0'
                        >
                          <X className='h-4 w-4' />
                        </button>
                      )}
                    </div>
                    <input type='file' accept='image/jpeg,image/png,image/gif,image/webp,application/pdf' className='sr-only' onChange={handleFileChange} />
                  </label>
                </div>
              )}
            </div>

            <Button type='submit' className='w-full'>
              Send Message
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
